import { Injectable, inject } from "@angular/core";
import { AbstractControl } from "@angular/forms";

import { DatabaseService } from 'src/app/servicios/database/database.service';
import { FsessionService } from '../../servicios/session/fsession.service';
import { SupportedLanguages } from "src/app/models";
import { configuracion } from 'src/configuracion';
import { CalculateRouteDirectionsResponse, Models, SearchAddressReverseResponse } from "azure-maps-rest";

@Injectable({
  providedIn: "root"
})
export class SharedService {
  private _databaseService = inject(DatabaseService);
  private _fsessionService = inject(FsessionService);


  /** Valida si un campo de formulario tiene espacios en blanco */
  validateThatThePropertyIsNotJustBlanks(control: AbstractControl) {
    return control.value.trim().length === 0 && control.value !== ""
      ? { propertyBlank: true }
      : null;
  }

  /**
   * Valida input para que solo permita letras y no permita espacios al inicio ni al final
   * @param control contiene el valor del campo de formulario a validar.
   * @returns
   */
  validateAlphabeticInput(control: AbstractControl) {
    let input = control.value;
    if (/^( +)/.test(input)  || /\s$/.test(input)) {
      return { 'error': 'validate_inputEspacios' };
    }
    if (/[^A-Za-z ]+/.test(input)) {
      return { 'error': 'validate_inputABC' };
    }
    return null;
  }

  /**
   * Valida input para que permita letras y números, y no permita espacios al inicio ni al final
   * @param control contiene el valor del campo de formulario a validar.
   * @returns Input validado
   */
  validateAlphanumericInput(control: AbstractControl) {
    let input = control.value;
    if (/^( +)/.test(input)  || /\s$/.test(input)) {
      return { 'error': 'validate_inputEspacios' };
    }
    if (!/^[A-Za-z0-9]+$/.test(input)) {
      return { 'error': 'validate_inputAlfanumerico' };
    }
    return null;
  }

  /**
   * Valida input para que solo permita letras, espacios intermedios y los caracteres especiales "-" y "_".
   * No permite espacios al inicio ni al final.
   * @param control contiene el valor del campo de formulario a validar.
   * @returns un objeto de error si la validación falla, o null si pasa la validación.
   */
  validateAlphabeticInputDescripcion(control: AbstractControl) {
    let input = control.value;
    if (/^( +)/.test(input) || /\s$/.test(input)) {
      return { 'error': 'validate_inputEspacios' };
    }
    if (/[^A-Za-z _-]+/.test(input)) {
      return { 'error': 'validate_inputABC' };
    }
    return null;
  }
  
  /**
   * Valida input para que solo permita letras, números, y los caracteres especiales "-" y "_".
   * No permite espacios al inicio ni al final.
   * @param control contiene el valor del campo de formulario a validar.
   * @returns un objeto de error si la validación falla, o null si pasa la validación.
   */
  validateAlphabeticNumberInputDescripcion(control: AbstractControl) {
    let input = control.value;
    if (/^( +)/.test(input) || /\s$/.test(input)) {
      return { 'error': 'validate_inputEspacios' };
    }
    const regex = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9 _-]+$/;

    if (!regex.test(input)) {
      return { 'error': 'validate_inputAlfanumerico' };
    }
    return null;
  }

  /**
   * Valida input para que solo permita letras, números, espacios intermedios, y los caracteres especiales "-", "_", ",", ".", ":", ";", "%", "ñ", "á" y "#".
   * No permite espacios al inicio ni al final.
   * @param control contiene el valor del campo de formulario a validar.
   * @returns un objeto de error si la validación falla, o null si pasa la validación.
   */
  validateDescripcionExtendida(control: AbstractControl) {
    const input = control.value;
    if (input === null || input === undefined || input === '') {
      return null;
    }

    if (/^( +)/.test(input) || /\s$/.test(input)) {
      return { 'error': 'validate_inputEspacios' };
    }

    const regex = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9 _.,:;()%#\-\u00C0-\u017F]+$/;

    if (!regex.test(input)) {
      return { 'error': 'validate_inputAlfanumericoExtendido' };
    }

    return null;
  }

  /**
   * Valida input para que permita letras y números, y no permita espacios al inicio ni al final ni espacios entre caracteres, ademas de añadir una longitud maxima de 7
   * @param control contiene el valor del campo de formulario a validar.
   * @returns Input validado
   */
  validateLicensePlates(control: AbstractControl) {
    let input = control.value;
  
    if (/^\s+|\s+$/.test(input)) {
      return { 'error': 'validate_inputEspacios' };
    }

    if (!/^[A-Za-z0-9]+$/.test(input)) {
      return { 'error': 'validate_inputAlfanumerico' };
    }

    if (input !== null) {
      if(input.length > 7) return { 'error': 'validate_maxLengthExceeded' };
    }
  
    return null;
  }
  

  /**
   * Valida input para que solo permita numeros y no permita ningun tipo de espacios
   * @param control contiene el valor del campo de formulario a validar.
   * @returns
   */
  validateNumericInput(control: AbstractControl) {
    let input = control.value;
    if (/\D/.test(input)) {
      return { 'error': 'validate_inputNumerico' };
    }
    if (/\s/.test(input)) {
      return { 'error': 'validate_inputEspacios' };
    }
    return null;
  }

  /**
   * Valida input para que solo permita números y el carácter punto (.) sin permitir ningún tipo de espacios
   * @param control contiene el valor del campo de formulario a validar.
   * @returns un objeto de error si la validación falla, o null si pasa la validación
   */
  validateNumericInputWithDecimal(control: AbstractControl) {
    let input = control.value;
    if (/\s/.test(input)) {
      return { 'error': 'validate_inputEspacios' };
    }
    if (/[^0-9.]/.test(input)) {
      return { 'error': 'validate_inputNumerico' };
    }
    return null;
  }

  /**
   * Valida input para que solo permita números, el carácter punto (.) y el guion (-),
   * sin permitir espacios.
   * @param control contiene el valor del campo de formulario a validar.
   * @returns un objeto de error si la validación falla, o null si pasa la validación.
   */
  validateNumericInputLongitudLatitud(control: AbstractControl) {
    const input = control.value;
    if (input === null || input === undefined || input === '') {
      return null;
    }

    const inputStr = String(input).trim();
    if (inputStr === '') return null;
    const normalized = inputStr.replace(',', '.');

    // No permitir espacios
    if (/\s/.test(normalized)) {
      return { 'error': 'validate_inputEspacios' };
    }

    // Solo permitir dígitos, un punto y un guion
    if (/[^0-9.\-]/.test(normalized)) {
      return { 'error': 'validate_inputLongLat' };
    }

    // Validar que no haya más de un guion "-"
    if ((normalized.match(/\-/g) || []).length > 1) {
      return { 'error': 'validate_inputGuion' };
    }

    // Validar que no haya más de un punto "."
    if ((normalized.match(/\./g) || []).length > 1) {
      return { 'error': 'validate_inputPunto' };
    }

    return null;
  }

  /**
   * Realiza peticion al servidor para obtener un nuevo token y guardarlo en el storage
   * @returns boolean
   */
  setUserToken(): Promise<boolean> {

    return new Promise((resolve) => {

      this._databaseService.Municipios().subscribe({
        next: ({ municipios, userInfo }: any) => {
          this._fsessionService.agregarActualizar('dato1', userInfo.accessToken);
          resolve(true);
        },
        error: () => {
          resolve(false);
        }
      });

    });

  }

  /**
   * Genera un archivo de traduccion tipo JSON con los cambios realizados
   * @param fileTranslation archivo de traduccion
   * @param language idioma del archivo
   * @returns FormData
   */
  generateFileTranslation( fileTranslation: {[key: string]: string}, language: SupportedLanguages ): FormData {

    const fileConvertJSONString = JSON.stringify(fileTranslation);
    const blob = new Blob([fileConvertJSONString], { type: 'application/json' });
    const file = new File([blob], `${language}.json`, { type: 'application/json' });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', file.name);
    return formData;

  }

  /**
   * Genera un archivo de traduccion tipo JSON de un nuevo idioma
   * @param fileTranslation archivo de traduccion
   * @param language idioma del archivo
   * @returns FormData
   */
  generateFileTranslationNewLanguage( fileTranslation: {[key: string]: string}, language: string ): FormData {

    const fileConvertJSONString = JSON.stringify(fileTranslation);
    const blob = new Blob([fileConvertJSONString], { type: 'application/json' });
    const file = new File([blob], `${language}.json`, { type: 'application/json' });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', file.name);
    return formData;

  }

  /**
   * Valida el nombre del archivo del nuevo idioma
   * @param control contiene el valor del campo de formulario a validar
   * @returns validacion de input
   */
  validateNameNewLanguage(control: AbstractControl) {

    const controlValue = (control.value as string).toLowerCase();
    const allowedLanguages: SupportedLanguages[] = ['es', 'en', 'ca', 'fr', 'pv'];

    if ( controlValue.trim() !== '' && allowedLanguages.find( (language) => controlValue === language ) ) {
      return { languageNotAllowed: true };
    }
    return null;

  }

  /**
   * Valida si un archivo es permitido con base en las extensiones recibidas
   * @param file archivo a validar
   * @param extensions extensiones permitidas (png, jpg, jpeg, etc...)
   */
  validateFileType( file: File, extensions: string[] ): boolean {

    if ( !extensions.includes( file.type.split('/')[1]?.toLowerCase() ) ) {
      return false;
    }
    return true;

  }

  /**
   * Validador personalizado que verifica si en el input existe al menos un apóstrofe.
   * @param control Control del formulario que se esta validando.
   * @returns Un objeto con la propiedad "propertyExistsApostrophes" en true si existe al menos un apóstrofe, o null en caso de que no exista.
   */
  validationExistsApostrophesInInput(control: AbstractControl) {

    const regex = RegExp("'+");
    return regex.test(control.value) ? { propertyExistsApostrophes: true } : null;

  }

  /**
   * Encripta data
   * @param data string a encriptar.
   * @returns string encriptado.
   */
  encryptData( data: string ): string {
    return data;
  }  

  /**
   * Realiza una busqueda en Azure Maps para obtener el nombre de la calle en las coordenadas dadas.
   * @param latitude Latitud de la coordenada.
   * @param longitude Longitud de la coordenada.
   * @returns El nombre de la calle o una cadena vacia si no se puede encontrar.
   */
  getStreetByCoordinates( latitude: number, longitude: number ): Promise<string> {
    
    return new Promise( async (resolve) => {

        try {
          const searchAzureMapsServiceUrl = `https://atlas.microsoft.com/search/address/reverse/json?api-version=1.0&subscription-key=${configuracion.ApiKey}&query=${latitude},${longitude}`;
          const response = await fetch(searchAzureMapsServiceUrl, { method: 'GET' });
          const data: SearchAddressReverseResponse = await response.json();
          const address = data.addresses[0]?.address;
          if ( address?.streetName ) {
            resolve( address.streetName );
          } else if ( address?.freeformAddress ) {
            resolve( address.freeformAddress );
          } else {
            resolve('');
          }
        } catch {
          resolve('');
        }
    });
  }

    /**
     * Crea una ruta utilizando una lista de puntos de ruta y envía
     * una solicitud a un servicio de enrutamiento para recuperar la información de la ruta.
     */
  async armarRuta(waypoints): Promise<{ summary: Models.RouteDirectionsSummary, coords: [number, number][] } | null> {
    return new Promise(async (resolve) => {
      try {
        const query = [];
        const defaultOrder = [];

        for (let i = 0; i < waypoints.length; i++) {
          query.push(waypoints[i]["Latitud"] + "," + waypoints[i]["Longitud"]);
          defaultOrder.push(i);
        }

        const waypointQuery = query.join(":");

        const restRoutingRequestUrl =
          `https://atlas.microsoft.com/route/directions/json?api-version=1&subscription-key=${configuracion.ApiKey}&query=${waypointQuery}&routeRepresentation=polyline&travelMode=car&view=Auto&computeBestOrder=true&ComputeTravelTime=all`;

        const response = await fetch(restRoutingRequestUrl, { method: 'GET' });
        const data: CalculateRouteDirectionsResponse = await response.json();

        const route = data.routes[0];
        let routeCoordinates = [];

        for (let legIndex = 0; legIndex < route.legs.length; legIndex++) {
          const leg = route.legs[legIndex];
          const legCoordinates = leg.points.map(function (point) {
            return [point.longitude, point.latitude];
          });
          routeCoordinates = routeCoordinates.concat(legCoordinates);
        }

        if (routeCoordinates.length > 0) {
          resolve({
            summary: route.summary,
            coords: routeCoordinates
          });
        } else {
          resolve(null);
        }
      } catch (error) {
        resolve(null);
      }
    });
  }

  /**
   * Valida input para asegurarse de que no contiene comas.
   * @param control contiene el valor del campo de formulario a validar.
   * @returns Input validado
   */
  validateNoCommaInInput(control: AbstractControl) {
    let input = control.value;

    if (/,/.test(input)) {
      return { 'error': 'error_no_comma_allowed' };
    }

    return null;
  }

  /**
   * Verifica el estado de permiso del microfono en el navegador.
   * @returns Promesa que se resuelve con el estado de permiso, que puede ser:
   * - granted: El permiso ha sido concedido.
   * - prompt: El permiso esta pendiente de ser concedido.
   * - denied: El permiso ha sido denegado.
   */
  async checkMicrophonePermissionStatus(): Promise<'granted' | 'prompt' | 'denied'> {

    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      return permissionStatus.state;
    } catch {
      return 'denied';
    }

  }

  /**
   * Convierte el tamaño en bytes a una cadena legible, con la unidad de medida
   * correspondiente.
   * @param bytes Tamaño en bytes del archivo.
   * @returns Cadena con el tama o del archivo, con la unidad de medida
   * correspondiente (Bytes, KB, MB, GB, TB, PB, EB, ZB, YB).
   */
  formatFileSize( bytes: number ): string {

    if ( bytes == 0 ) return '0 Bytes';
    const conversionFactor = 1000;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const unit = Math.floor( Math.log(bytes) / Math.log(conversionFactor) );
    return parseFloat((bytes / Math.pow(conversionFactor, unit)).toFixed(2)) + ' ' + sizes[unit];

  }

  /**
   * Elimina los diacríticos de una cadena de texto.
   * @param text Cadena de texto que puede contener caracteres con diacríticos.
   * @returns La cadena de texto sin diacríticos.
   */
  removeDiacritics(text: string): string {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  /**
   * Valida variable de permiso: sin espacios (ni inicio, ni intermedio, ni final, ni dobles), solo letras, números y guión bajo.
   * @param control contiene el valor del campo de formulario a validar.
   * @returns un objeto de error si la validación falla, o null si pasa.
   */
  validateVariableFormatSpecialPermission(control: AbstractControl): { [key: string]: boolean } | null {
    const input = control.value;
    if (input === null || input === undefined || input === '') return null;
    const str = String(input);
    if (str.length === 0) return null;
    if (/\s/.test(str)) return { validate_variable_sin_espacios: true };
    if (!/^[a-zA-Z0-9_]+$/.test(str)) return { validate_variable_solo_letras_guion: true };
    return null;
  }

  /**
   * Valida variable de parámetro backend: sin espacios, mayúsculas/números/guión bajo,
   * debe empezar por letra y tener entre 3 y 100 caracteres (ej. API_GLOBAL_TIMEOUT_MS).
   * @param control contiene el valor del campo de formulario a validar.
   * @returns un objeto de error si la validación falla, o null si pasa.
   */
  validateBackendParamVariable(control: AbstractControl): { [key: string]: boolean } | null {
    const input = control.value;
    if (input === null || input === undefined || input === '') return null;

    const str = String(input);
    if (/\s/.test(str)) return { validate_variable_sin_espacios: true };

    const normalized = str.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9_]{2,99}$/.test(normalized)) {
      return { validate_backend_param_variable: true };
    }

    return null;
  }

  /**
   * Valida key_identity: debe iniciar con "sp_", sin espacios en ningún lugar, solo letras, números y guión bajo.
   * @param control contiene el valor del campo de formulario a validar.
   * @returns un objeto de error si la validación falla, o null si pasa.
   */
  validateKeyIdentitySpecialPermission(control: AbstractControl): { [key: string]: boolean } | null {
    const input = control.value;
    if (input === null || input === undefined || input === '') return null;
    const str = String(input);
    if (str.length === 0) return null;
    if (/\s/.test(str)) return { validate_variable_sin_espacios: true };
    if (!str.startsWith('sp_')) return { validate_key_identity_debe_sp: true };
    if (!/^sp_[a-zA-Z0-9_]+$/.test(str)) return { validate_variable_solo_letras_guion: true };
    return null;
  }

  /**
   * Valida descripcion: debe iniciar con "perm_", sin espacios en ningún lugar, solo letras, números y guión bajo.
   * @param control contiene el valor del campo de formulario a validar.
   * @returns un objeto de error si la validación falla, o null si pasa.
   */
  validateDescripcionPermisoSpecialPermission(control: AbstractControl): { [key: string]: boolean } | null {
    const input = control.value;
    if (input === null || input === undefined || input === '') return null;
    const str = String(input);
    if (str.length === 0) return null;
    if (/\s/.test(str)) return { validate_variable_sin_espacios: true };
    if (!str.startsWith('perm_')) return { validate_descripcion_debe_perm: true };
    if (!/^perm_[a-zA-Z0-9_]+$/.test(str)) return { validate_variable_solo_letras_guion: true };
    return null;
  }

  /*
   * Abre un elemento en modo fullscreen con soporte cross-browser
   * @param element elemento a pantalla completa
   * @returns Promise que se resuelve cuando entra en fullscreen
   */
  requestFullscreenCrossBrowser( elementRef: HTMLElement ): Promise<void> {
    if (!elementRef) return Promise.reject('Element not found');

    const requestMethod = 
      elementRef.requestFullscreen ||
      (elementRef as any).mozRequestFullScreen ||
      (elementRef as any).webkitRequestFullscreen ||
      (elementRef as any).msRequestFullscreen;

    if (requestMethod) {
      return requestMethod.call(elementRef);
    }

    return Promise.reject('Fullscreen not supported');
  }

}